import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import client, { resolveApiAssetUrl } from '../../lib/api';
import { useGameLauncher } from '../../hooks/useGameLauncher';
import { useSettings } from '../../context/SettingsContext';
import { useVisibleHomeCategories } from '../../lib/homeCategories';
import { cacheSliders, readCachedSliders } from '../../lib/sliderCache';
import { slidersAt } from '../../lib/sliderPlacement';
import useSwipe from '../../hooks/useSwipe';
import GameModal from '../../components/GameModal';
import GameChoiceModal from '../../components/GameChoiceModal';
import AuthModal from '../../components/AuthModal';
import ProviderStrip, { providersFromResponse } from '../../components/ProviderStrip';
import ParisSection from './ParisSection';
import ParisGameCard from './ParisGameCard';
import { SECTIONS, HOOKS, hook, sectionHook } from '../../lib/domHooks';
import HomeBlocks, { HomeBlock } from '../../components/HomeBlocks';

const STRIP_LIMIT = 14;
const SHORTCUT_LIMIT = 12;
// Referans arayüzde hero tek bannerdır ve sağdaki dikey kumandayla dönerdir.
const HERO_INTERVAL = 6000;

const mapGame = (game) => ({
  id: game.id,
  name: game.name,
  provider: game.provider,
  img: game.image,
  hot: game.hot,
  new: game.new,
  api_provider: game.api_provider,
  game_id: game.game_id,
  demo_supported: !!game.demo_supported,
});

const ParisHome = () => {
  const settings = useSettings();
  const gl = useGameLauncher();

  // Klasik vitrinle aynı önbellek: yanıt beklenirken son bannerlar çizilir,
  // taze liste gelince üzerine yazılır (bkz. lib/sliderCache.js).
  const [banners, setBanners] = useState(readCachedSliders);
  const [featured, setFeatured] = useState([]);
  const [popular, setPopular] = useState([]);
  const [fresh, setFresh] = useState([]);
  // Kategori şeritleri "+N" rozetini toplam oyun sayısından üretir; bu yüzden
  // yalnızca liste değil `total` da tutulur (`/games` yanıtı ikisini de verir).
  const [slots, setSlots] = useState({ items: [], total: 0 });
  const [live, setLive] = useState({ items: [], total: 0 });
  const [crash, setCrash] = useState({ items: [], total: 0 });
  const [providers, setProviders] = useState([]);
  const [heroPage, setHeroPage] = useState(0);
  const visibleCategories = useVisibleHomeCategories(settings.sports_enabled);

  useEffect(() => {
    const get = (url, params) => client.get(url, { params }).then(({ data }) => data).catch(() => null);
    const asList = (data) => ({
      items: data?.items || [],
      total: Number(data?.total) || 0,
    });

    // Ağ hatasında (`data === null`) önbellekten çizilen bannerlar korunur.
    get('/sliders').then((data) => {
      if (!data) return;
      cacheSliders(data.items || []);
      setBanners(data.items || []);
    });
    // Vitrin şeritleri panelden seçilir (Oyun Yönetimi → Ana Sayfa Vitrini);
    // hiç oyun işaretlenmemişse uç otomatik kaynakla doldurur, şerit boş kalmaz.
    get('/games/showcase', { strip: 'featured', category: 'slot', limit: SHORTCUT_LIMIT })
      .then((data) => setFeatured(data?.items || []));
    get('/games/showcase', { strip: 'popular', category: 'slot', limit: STRIP_LIMIT })
      .then((data) => setPopular(data?.items || []));
    get('/games/showcase', { strip: 'new', category: 'slot', limit: STRIP_LIMIT })
      .then((data) => setFresh(data?.items || []));
    get('/games', { category: 'slot', limit: STRIP_LIMIT }).then((data) => setSlots(asList(data)));
    get('/games', { category: 'live-casino', limit: STRIP_LIMIT }).then((data) => setLive(asList(data)));
    get('/games', { category: 'crash', limit: STRIP_LIMIT }).then((data) => setCrash(asList(data)));
    get('/game-providers', { category: 'slot' }).then((data) => {
      setProviders(providersFromResponse(data));
    });
  }, []);

  // Hero yalnızca görseli çizer (metin/CTA alanı yok); görselsiz banner burada
  // boş bir kutu olurdu, bu yüzden elenir.
  // Yalnızca ana slider banner'ları: 'side' ve katalog sayfası banner'ları
  // buraya düşmemelidir (bkz. lib/sliderPlacement.js).
  const visibleBanners = slidersAt(banners).filter(
    (banner) => (
      banner.image
      && (banner.cta_link !== '/spor' || settings.sports_enabled !== false)
    ),
  );
  const heroCount = visibleBanners.length;
  const activeHero = heroCount > 0 ? heroPage % heroCount : 0;
  const heroBanner = visibleBanners[activeHero];

  // Sayaç heroPage'e de bağlıdır: parmakla kaydıran oyuncu seçtiği banner'ı
  // tam süre görsün, otomatik geçiş kaydırmanın hemen ardına düşmesin.
  useEffect(() => {
    if (heroCount < 2) return undefined;
    const timer = setInterval(() => setHeroPage((page) => (page + 1) % heroCount), HERO_INTERVAL);
    return () => clearInterval(timer);
  }, [heroCount, heroPage]);

  // Mobilde banner parmakla çevrilir; sağdaki dikey kumanda dar ekranda
  // görselin üstüne binecek kadar küçüktür.
  const heroSwipe = useSwipe({
    enabled: heroCount > 1,
    onNext: () => setHeroPage((page) => (page + 1) % heroCount),
    onPrev: () => setHeroPage((page) => (page - 1 + heroCount) % heroCount),
  });

  return (
    <>
      <HomeBlocks theme="paris">
        <HomeBlock id={SECTIONS.HERO}>
        {heroBanner && (
          <div className="paris-hero" {...heroSwipe} {...sectionHook(SECTIONS.HERO)}>
            <Link
              key={activeHero}
              to={heroBanner.cta_link || '/promosyonlar'}
              className="paris-hero-item"
              {...hook(HOOKS.SLIDER)}
            >
              <img src={resolveApiAssetUrl(heroBanner.image)} alt={heroBanner.title || 'Kampanya'} />
            </Link>
            {/* Referans arayüzün imzası: sağ kenarda blur'lu dikey kumanda —
                yukarı ok, sayaç, aşağı ok. Tek banner varken çizilmez. */}
            {heroCount > 1 && (
              <div className="paris-hero-pager">
                <button
                  type="button"
                  className="paris-hero-arrow"
                  onClick={() => setHeroPage((page) => (page - 1 + heroCount) % heroCount)}
                  aria-label="Önceki banner"
                >
                  <ChevronUp size={20} />
                </button>
                <span className="paris-hero-count">
                  <strong>{activeHero + 1}</strong>
                  {' / '}
                  {heroCount}
                </span>
                <button
                  type="button"
                  className="paris-hero-arrow"
                  onClick={() => setHeroPage((page) => (page + 1) % heroCount)}
                  aria-label="Sonraki banner"
                >
                  <ChevronDown size={20} />
                </button>
              </div>
            )}
          </div>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.SHORTCUTS}>
        {/* Hero'nun altındaki kare kısayollar panelin "öne çıkan" seçimidir;
            aşağıdaki şeritler kendi vitrin etiketlerinden gelir, böylece hiçbir
            şerit bir diğerinin kopyası olmaz. */}
        {featured.length > 0 && (
          <div className="paris-shortcut-strip" {...sectionHook(SECTIONS.SHORTCUTS)}>
            {featured.map((game) => (
              <ParisGameCard
                key={game.id}
                game={mapGame(game)}
                onPlay={gl.play}
                variant="tile"
              />
            ))}
          </div>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.CATEGORIES}>
        {visibleCategories.length > 0 && (
          <div className="paris-tiles" {...sectionHook(SECTIONS.CATEGORIES)}>
            {visibleCategories.map((category) => (
              <Link
                key={category.id}
                to={category.href || '/'}
                className="paris-tile"
                {...hook(HOOKS.CATEGORY_CARD)}
              >
                <span className="paris-tile-copy">
                  <span className="paris-tile-title">{category.title}</span>
                  <span className="paris-tile-sub">{category.subtitle}</span>
                  <span className="paris-tile-cta">{category.cta_text}</span>
                </span>
                {category.image && (
                  <img
                    className="paris-tile-art"
                    src={resolveApiAssetUrl(category.image)}
                    alt=""
                    aria-hidden="true"
                  />
                )}
              </Link>
            ))}
          </div>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.POPULAR}>
        {popular.length > 0 && (
          <ParisSection section={SECTIONS.POPULAR} title="Trend" to="/slotlar">
            {popular.map((game) => (
              <ParisGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </ParisSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.SLOTS}>
        {slots.items.length > 0 && (
          <ParisSection
            section={SECTIONS.SLOTS}
            title="Casino Oyunları"
            to="/slotlar"
            count={slots.total}
            shown={slots.items.length}
          >
            {slots.items.map((game) => (
              <ParisGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </ParisSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.LIVE_CASINO}>
        {live.items.length > 0 && (
          <ParisSection
            section={SECTIONS.LIVE_CASINO}
            title="Canlı Casino"
            to="/canli-casino"
            count={live.total}
            shown={live.items.length}
          >
            {live.items.map((game) => (
              <ParisGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </ParisSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.NEW}>
        {fresh.length > 0 && (
          <ParisSection section={SECTIONS.NEW} title="Yeni Oyunlar" to="/slotlar">
            {fresh.map((game) => (
              <ParisGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </ParisSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.FAST}>
        {crash.items.length > 0 && (
          <ParisSection
            section={SECTIONS.FAST}
            title="Hızlı Oyunlar"
            to="/crash"
            count={crash.total}
            shown={crash.items.length}
          >
            {crash.items.map((game) => (
              <ParisGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </ParisSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.PROVIDERS}>
        <ProviderStrip providers={providers} classPrefix="paris" />
        </HomeBlock>
      </HomeBlocks>

      <GameChoiceModal
        game={gl.choice}
        user={gl.user}
        balance={gl.balance}
        bonusBalance={gl.bonusBalance}
        demoAvailable={gl.demoAvailable}
        onClose={gl.closeChoice}
        onReal={gl.startReal}
        onDemo={gl.startDemo}
        onLogin={() => gl.openAuth('login')}
        onRegister={() => gl.openAuth('register')}
      />
      {gl.activeGame && (
        <GameModal
          key={`${gl.activeGame.id}-${gl.mode}`}
          game={gl.activeGame}
          mode={gl.mode}
          onClose={gl.closeGame}
        />
      )}
      <AuthModal
        open={!!gl.authTab}
        onClose={() => gl.setAuthTab(null)}
        defaultTab={gl.authTab || 'login'}
      />
    </>
  );
};

export default ParisHome;
